import { useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { Button, EmptyState, ErrorState, InputText, Modal, Spinner, Table, Tag } from '../../components'
import type { TableColumn } from '../../components'
import { groupDetailPath } from '../../lib/routes'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import styles from './GroupsPage.module.scss'

type StationGroup = RouterOutputs['stations']['listGroups'][number]

/** The API's own cap on `name`, so a too-long name is not a round-trip. */
const NAME_MAX_LENGTH = 120

/**
 * The groups a manager shapes the plant with: a group bundles work-center TYPES
 * and is what people are assigned to, which makes it the authorization axis of
 * the whole system. There is no screen for machines and none for types on their
 * own — this list is the way in.
 *
 * Deactivating a group is a soft delete, so the list can hide one: the "ver
 * inactivos" toggle is not a convenience, it is the only way back to a group
 * somebody retired by mistake.
 */
export const GroupsPage = (): ReactElement => {
  const [includeInactive, setIncludeInactive] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const groupsQuery = trpc.stations.listGroups.useQuery({ includeInactive })

  const columns: Array<TableColumn<StationGroup>> = [
    {
      key: 'name',
      header: 'Grupo',
      // The name IS the way in: one 48px target per row instead of a name and an
      // "abrir" button competing for the same thumb.
      render: (group) => (
        <Link className={styles['groups-page__link']} to={groupDetailPath(group.id)}>
          {group.name}
        </Link>
      ),
    },
    {
      key: 'active',
      header: 'Estado',
      render: (group) => (
        group.active
          ? <Tag tone="ok" label="Activo" />
          : <Tag tone="warn" label="Inactivo" />
      ),
    },
  ]

  return (
    <section className={styles['groups-page']}>
      <header className={styles['groups-page__header']}>
        <h1 className={styles['groups-page__title']}>Grupos de estaciones</h1>
        <div className={styles['groups-page__actions']}>
          <Button
            variant={includeInactive ? 'primary' : 'secondary'}
            aria-pressed={includeInactive}
            onClick={() => setIncludeInactive((shown) => !shown)}
            label="Ver inactivos"
          />
          <Button onClick={() => setCreateOpen(true)} label="Crear grupo" />
        </div>
      </header>

      {groupsQuery.isPending && <Spinner size="page" label="Cargando grupos…" />}

      {groupsQuery.isError && (
        <ErrorState error={groupsQuery.error} onRetry={() => void groupsQuery.refetch()} />
      )}

      {groupsQuery.isSuccess && groupsQuery.data.length === 0 && (
        <EmptyState
          title="Aún no hay grupos"
          message="Un grupo reúne los tipos de estación que un operario puede elegir. Crea el primero para poder asignar personas."
          action={<Button onClick={() => setCreateOpen(true)} label="Crear grupo" />}
        />
      )}

      {groupsQuery.isSuccess && groupsQuery.data.length > 0 && (
        <Table
          caption="Grupos de estaciones"
          columns={columns}
          rows={groupsQuery.data}
          rowKey={(group) => group.id}
        />
      )}

      <CreateGroupDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  )
}

type CreateGroupDialogProps = {
  open: boolean
  onClose: () => void
}

/**
 * Creating a group is one field, so it is a dialog and not a screen of its own.
 *
 * `name` is unique in `app.station_groups`, and a repeated one comes back
 * CONFLICT: that is shown in place, next to the field, instead of closing the
 * dialog and losing what was typed.
 */
const CreateGroupDialog = ({ open, onClose }: CreateGroupDialogProps): ReactElement => {
  const utils = trpc.useUtils()
  const [name, setName] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const close = (): void => {
    setName('')
    setErrorMessage(null)
    onClose()
  }

  const createGroup = trpc.stations.createGroup.useMutation({
    onSuccess: () => {
      // Both variants of the list: the toggle may be on either side of it.
      void utils.stations.listGroups.invalidate()
      close()
    },
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  const trimmed = name.trim()

  return (
    <Modal open={open} title="Crear grupo" onClose={close} maxWidth="44rem">
      <form
        className={styles['groups-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          setErrorMessage(null)
          createGroup.mutate({ name: trimmed })
        }}
      >
        <InputText
          label="Nombre del grupo"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          hint="Cómo lo llama planta: «Marmitas», «Montaje frío»…"
          onChange={(event) => setName(event.target.value)}
          required
        />
        {errorMessage !== null && (
          <p role="alert" className={styles['groups-page__error']}>{errorMessage}</p>
        )}
        <Button
          type="submit"
          fullWidth
          disabled={createGroup.isPending || trimmed === ''}
          label={createGroup.isPending ? 'Creando…' : 'Crear'}
        />
      </form>
    </Modal>
  )
}
